import fetch from '../utils/fetch';

const BaseUrl = process.env.API_BASE || process.env.MONO_BASE;

const getUrl = (path) => `${BaseUrl}${path}`;

export default {
  /**
   * Get list of graphic templates with faceted search and filtering.
   * Not to be confused with our current naming for graphic templates.
   * These are related to the new Packs & Templates API (i.e. presets).
   * @param {Object} opts
   * @param {Number} opts.page - Page number to fetch
   * @param {Number} opts.limit - Number of results per page
   * @param {String} opts.assetType - Filter by asset type
   * @param {String} opts.search - Search query
   * @param {Array<String>} opts.verticals - Filter by verticals
   * @param {Array<String>} opts.storyTypes - Filter by story types
   * @param {String} opts.theme - Filter by theme
   * @param {Array<String>} opts.dataTypes - Filter by data types
   * @param {String} opts.layout - Filter by layout
   * @param {Array<String>} opts.signals - Filter by signals
   * @param {String} opts.sortBy - Field to sort by
   * @param {String} opts.sortDir - Direction to sort by
   * @returns {Promise<{total: Number, templates: Array, facets: Object}>}
   */
  search(opts = {}) {
    const {
      page = 1,
      limit = 100,
      type = 'template',
      assetType = 'graphic',
      search = '',
      verticals = [],
      storyTypes = [],
      theme = '',
      dataTypes = [],
      layout = '',
      layouts = [], // Flatten down to just `layout` in params.
      signals = [],
      sortBy = 'createdAt',
      sortDir = 'DESC',
    } = opts;

    const query = {
      page,
      limit,
      type,
      assetType,
      search,
      sortBy,
      sortDir,
    };

    if (verticals.length) {
      query.verticals = verticals.join(',');
    }

    if (storyTypes.length) {
      query.storyTypes = storyTypes.join(',');
    }

    if (dataTypes.length) {
      query.dataTypes = dataTypes.join(',');
    }

    if (signals.length) {
      query.signals = signals.join(',');
    }

    if (theme) {
      query.theme = theme;
    }

    if (layout) {
      query.layout = layout;
    } else if (layouts?.length) {
      [query.layout] = layouts;
    }

    return fetch(getUrl('/packs/search/all'), { query });
  },

  /**
   * Fetch a single Template by ID.
   * Not to be confused with our current naming for graphic templates.
   * These are related to the new Packs & Templates API (i.e. presets).
   * @param {Number} id - Template ID.
   * @returns {Promise<Object>} Promise resolving to Template object with full settings.
   */
  getTemplate(id) {
    if (!id) {
      throw new Error('Missing required ID param');
    }

    return fetch(getUrl(`/templates/${id}`));
  },

  /**
   * Create a new template
   * Not to be confused with our current naming for graphic templates.
   * These are related to the new Packs & Templates API (i.e. presets).
   * @param {Object} template - Template data to create
   * @returns {Promise<Object>} Promise resolving to created Template object
   */
  createTemplate(template) {
    if (!template) {
      throw new Error('Missing required template param');
    }

    return fetch(getUrl('/templates'), {
      method: 'POST',
      body: JSON.stringify(template),
    });
  },

  /**
   * Update an existing template by ID
   * Not to be confused with our current naming for graphic templates.
   * These are related to the new Packs & Templates API (i.e. presets).
   * @param {Number} id - Template ID
   * @param {Object} updates - Partial template data to update
   * @returns {Promise<Object>} Promise resolving to update response
   */
  updateTemplate(id, updates) {
    if (!id) {
      throw new Error('Missing required ID param');
    }

    if (!updates) {
      throw new Error('Missing required updates param');
    }

    return fetch(getUrl(`/templates/${id}`), {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },
};
